/**
 * Organic primitives — the curved half of doc 10's shape language.
 *
 * `render/primitives.ts` builds the player's world: diamonds, prisms, cylinders,
 * everything rectilinear and anchored to a footprint. Nothing there describes a
 * body. These do, and they are just as strict about Law L4: every helper takes
 * and returns whole pixels, and the animation offsets are quantized here rather
 * than by nineteen species remembering to round.
 *
 * Nothing in this file knows what a Gnasher is. Species are compositions.
 */

import type { Color, PixelSurface } from '../render/surface.ts';

/**
 * The animation clock. A creature's `phase` is a float 0..1; every moving part
 * reads it through one of these so the whole bestiary shares a beat and every
 * offset is an integer before it reaches a draw call.
 */
export function step(phase: number, steps: number): number {
  const p = phase - Math.floor(phase);
  return Math.min(steps - 1, Math.floor(p * steps));
}

/** The classic four-beat tail flick: 0, +1, 0, -1. */
export function flick(phase: number): number {
  return [0, 1, 0, -1][step(phase, 4)]!;
}

/** A two-beat swell for things that pulse rather than swim: 0, +1, +1, 0. */
export function swell(phase: number): number {
  return [0, 1, 1, 0][step(phase, 4)]!;
}

/** Row half-widths of a rough ellipse `ry` tall — the spine of every body here. */
export function blobRows(rx: number, ry: number): number[] {
  const rows: number[] = [];
  for (let y = -ry; y <= ry; y++) {
    const t = 1 - (y * y) / ((ry + 0.5) * (ry + 0.5));
    rows.push(Math.max(1, Math.round(rx * Math.sqrt(Math.max(0, t)))));
  }
  return rows;
}

/**
 * A two-tone body mass: lit above the mid-line, shaded below it. One op per
 * row, which is what keeps a boss inside the 400-op creature budget.
 */
export function blob(
  s: PixelSurface,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  back: Color,
  belly: Color,
): void {
  const rows = blobRows(rx, ry);
  rows.forEach((hw, k) => {
    s.hline(cx - hw, cx + hw - 1, cy - ry + k, k < rows.length / 2 ? back : belly, 'set');
  });
}

/**
 * A dome: the top of a blob with the bottom cut away. Bells, carapaces and
 * anything that sits over a fringe rather than closing under one.
 */
export function dome(
  s: PixelSurface,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  back: Color,
  belly: Color,
  keep = 0.66,
): void {
  const rows = blobRows(rx, ry);
  rows.forEach((hw, k) => {
    if (k > rows.length * keep) return;
    s.hline(cx - hw, cx + hw - 1, cy - ry + k, k < rows.length * 0.4 ? back : belly, 'set');
  });
}

/**
 * An undulating tube swimming east, drawn column by column from `x0` to `x1`.
 * `wave` is sampled per column and offset by the phase, which is what makes an
 * eel read as one continuous edge instead of a stack of segments.
 */
export function spine(
  s: PixelSurface,
  cx: number,
  cy: number,
  x0: number,
  x1: number,
  wave: number[],
  phase: number,
  half: (k: number) => number,
  back: Color,
  belly: Color,
): void {
  const shift = step(phase, wave.length);
  for (let k = x0; k <= x1; k++) {
    const off = wave[(((k + shift) % wave.length) + wave.length) % wave.length]!;
    const h = half(k);
    if (h < 0) continue;
    s.vline(cx + k, cy - h + off, cy + h + off, k % 2 ? back : belly, 'set');
  }
}

/** A tail fan: `n` tapering vertical runs behind the body, beating with the phase. */
export function fan(
  s: PixelSurface,
  x: number,
  cy: number,
  n: number,
  spread: number,
  beat: number,
  color: Color,
): void {
  for (let k = 0; k < n; k++) {
    const h = spread - Math.round((k * spread) / n);
    s.vline(x + (k & 1) - n + k, cy - h + beat, cy + h + beat, color, 'set');
  }
}

/**
 * Legs hanging under a body, alternating up and down with the phase. `lift` is
 * the stride height: 1 for a resting shuffle, 2 for a walk. It is the walker's
 * half of what `tailThrow` does for swimmers, and it is the whole difference
 * between an `idle` frame and a `move` one for anything with feet.
 */
export function legs(
  s: PixelSurface,
  cx: number,
  cy: number,
  xs: number[],
  w: number,
  h: number,
  phase: number,
  color: Color,
  lift = 1,
): void {
  const beat = step(phase, 2);
  for (const dx of xs) {
    const up = (dx + beat) % 2 === 0 ? 0 : lift;
    s.fillRect(cx + dx, cy + up, w, h, color, 'set');
  }
}

/**
 * A row of teeth along an open jaw. Teeth are `bone`, never white — Law L5 has
 * no room for a colour that exists only in mouths.
 */
export function teeth(
  s: PixelSurface,
  cx: number,
  cy: number,
  n: number,
  pitch: number,
  color: Color,
): void {
  for (let k = 0; k < n; k++) s.px(cx + k * pitch, cy, color);
}
